/**
 * @typedef {import('@/types/structured-data-editor').SdeColumnDef} SdeColumnDef
 * @typedef {import('@/types/structured-data-editor').SdeEditorConfig} SdeEditorConfig
 */

/**
 * @param {number|object|null|undefined} value
 * @returns {number|null}
 */
function resolveProviderId(value) {
    if (value == null) {
        return null;
    }
    if (typeof value === "object") {
        return value.id ?? null;
    }
    return value;
}

/**
 * Restrict editor columns to mapped CSV fields (+ config mandatory keys).
 * Ignored mapping slots must not appear as empty columns in the review modal.
 *
 * @param {SdeEditorConfig} config
 * @param {(string|null|undefined)[]} fieldsMapping
 * @returns {SdeEditorConfig}
 */
export function filterSdeConfigToMappedColumns(config, fieldsMapping) {
    const mappedKeys = new Set(
        (fieldsMapping || []).filter((key) => key != null && key !== "")
    );
    if (!mappedKeys.size) {
        return config;
    }
    (config.mandatoryKeys || []).forEach((key) => mappedKeys.add(key));
    return {
        ...config,
        columns: (config.columns || []).filter((column) => mappedKeys.has(column.key)),
    };
}

/**
 * @param {string} syncType
 * @param {{
 *   providerId?: number|null,
 *   providerName?: string|null,
 *   pricesIncludeVat?: boolean,
 *   vatRate?: number|null,
 *   productTypes?: object[],
 *   fieldDefs?: object[],
 *   bulkEdit?: boolean,
 * }} context
 * @returns {SdeEditorConfig}
 */
export function buildSdeConfig(syncType, context = {}) {
    switch (syncType) {
        case "providerproducts":
            return buildProviderProductsConfig(context);
        case "products":
            return buildProductsConfig(context);
        case "customers":
            return buildCustomersConfig(context);
        case "providers":
            return buildProvidersConfig(context);
        case "bundles":
            return buildFieldDefsSyncConfig("bundles", context, ["bundle_code"]);
        case "allocations":
            return buildFieldDefsSyncConfig("allocations", context, ["upc"]);
        case "stockproducts":
            return buildFieldDefsSyncConfig("stockproducts", context, ["product"]);
        case "productcatalogentries":
            return buildFieldDefsSyncConfig("productcatalogentries", context, ["upc"]);
        case "destock":
            return buildFieldDefsSyncConfig("destock", context, ["upc", "quantity"]);
        default:
            return buildFieldDefsSyncConfig(syncType, context, ["name"]);
    }
}

/**
 * @param {object} context
 * @returns {SdeEditorConfig}
 */
function buildProviderProductsConfig(context) {
    const columns = buildColumnsFromFieldDefs(context.fieldDefs || defaultProviderProductFieldDefs());
    const providerId = resolveProviderId(context.providerId ?? context.provider);
    return {
        syncType: "providerproducts",
        entityLabel: context.providerName || "",
        columns,
        mandatoryKeys: ["name"],
        extraParams: providerId ? { provider: providerId } : {},
        pricesIncludeVat: context.pricesIncludeVat ?? false,
        vatRate: context.vatRate ?? null,
        bulkEdit: Boolean(context.bulkEdit),
        showInstanceIdColumn: Boolean(context.bulkEdit),
    };
}

/**
 * @param {object} context
 * @returns {SdeEditorConfig}
 */
function buildProductsConfig(context) {
    const columns = buildColumnsFromFieldDefs(context.fieldDefs || defaultProductsFieldDefs());
    return {
        syncType: "products",
        entityLabel: context.entityLabel || "",
        columns,
        // UPC identifies create vs update; name is only required on create (server).
        mandatoryKeys: ["upc"],
        extraParams: {},
        pricesIncludeVat: context.pricesIncludeVat ?? false,
        vatRate: context.vatRate ?? null,
        bulkEdit: Boolean(context.bulkEdit),
        showInstanceIdColumn: Boolean(context.bulkEdit),
    };
}

/**
 * @param {object} context
 * @returns {SdeEditorConfig}
 */
function buildCustomersConfig(context) {
    return buildFieldDefsSyncConfig("customers", context, ["name"]);
}

/**
 * @param {object} context
 * @returns {SdeEditorConfig}
 */
function buildProvidersConfig(context) {
    return buildFieldDefsSyncConfig("providers", context, ["name"]);
}

/**
 * @param {string} syncType
 * @param {object} context
 * @param {string[]} mandatoryKeys
 * @returns {SdeEditorConfig}
 */
function buildFieldDefsSyncConfig(syncType, context, mandatoryKeys) {
    const columns = buildColumnsFromFieldDefs(context.fieldDefs || []);
    return {
        syncType,
        entityLabel: context.entityLabel || context.providerName || "",
        columns,
        mandatoryKeys,
        extraParams: context.extraParams || {},
        pricesIncludeVat: context.pricesIncludeVat ?? false,
        vatRate: context.vatRate ?? null,
        bulkEdit: Boolean(context.bulkEdit),
    };
}

/**
 * @param {string} key
 * @param {(key: string) => string} translate
 * @param {string} [fallback]
 * @param {string|null} [syncType]
 * @returns {string}
 */
export function resolveSdeStandardColumnLabel(key, translate, fallback = "", syncType = null) {
    if (!key || String(key).startsWith("metadata.")) {
        return fallback || key;
    }
    let i18nKey = `sde.columns.${key}`;
    // Catalog sell-price imports reuse unit_amount, but the shared column label is
    // purchase-oriented (provider products). Prefer a sell-price label here.
    if (syncType === "productcatalogentries" && key === "unit_amount") {
        i18nKey = "sde.columns.unit_amount_sell";
    }
    // Catalog entry sync only has sale_format_code (no separate label column).
    // Show the friendlier "Sale format" label instead of "Sale format code".
    if (syncType === "productcatalogentries" && key === "sale_format_code") {
        i18nKey = "sde.columns.sale_format_label";
    }
    // Distinguish catalog-entry fields from linked product columns in CSV export.
    if (syncType === "productcatalogentries" && key === "label") {
        i18nKey = "sde.columns.catalog_entry_label";
    }
    if (syncType === "productcatalogentries" && key === "description") {
        i18nKey = "sde.columns.catalog_entry_description";
    }
    if (syncType === "productcatalogentries" && key === "manufacturer") {
        i18nKey = "sde.columns.catalog_entry_manufacturer";
    }
    const translated = translate(i18nKey);
    if (translated && translated !== i18nKey) {
        return translated;
    }
    return fallback || key;
}

/**
 * @param {SdeColumnDef} column
 * @param {(key: string) => string} translate
 * @param {string|null} [syncType]
 * @returns {SdeColumnDef}
 */
export function translateSdeColumn(column, translate, syncType = null) {
    return {
        ...column,
        label: resolveSdeStandardColumnLabel(
            column.key,
            translate,
            column.label,
            syncType
        ),
    };
}

const READONLY_FIELD_KEYS = new Set(["format_label", "sale_format_label"]);

const FORMAT_FIELD_KEYS = new Set(["format_code", "sale_format_code"]);

const STOCK_UNIT_CODES = ["pc", "kg", "g", "L", "cl", "ml"];
const PIECE_CONTENT_UNIT_CODES = ["kg", "g", "L", "cl", "ml"];

const UNIT_FIELD_CHOICES = {
    stock_unit: STOCK_UNIT_CODES,
    sale_unit: STOCK_UNIT_CODES,
    piece_content_unit: PIECE_CONTENT_UNIT_CODES,
    sale_unit_content_unit: STOCK_UNIT_CODES,
    sale_content_unit: PIECE_CONTENT_UNIT_CODES,
};

const NUMBER_FIELD_KEYS = new Set([
    "packaging",
    "piece_content_quantity",
    "sale_unit_content_quantity",
    "sale_content_quantity",
    "low_stock_alert_treshold",
    "unit_amount",
    "unit_amount_excl_vat",
    "default_unit_purchase_price_excl_vat",
    "unit_agent_commission",
    "vat_rate",
]);

/**
 * @param {object[]} fieldDefs
 * @returns {SdeColumnDef[]}
 */
function buildColumnsFromFieldDefs(fieldDefs) {
    return fieldDefs.map((field) => ({
        key: field.value,
        label: field.text || field.value,
        kind: String(field.value).startsWith("metadata.") ? "metadata" : "standard",
        fieldType: mapFieldType(field.type, field.value),
        unit: field.unit || undefined,
        choices: resolveColumnChoices(field),
        editable: !READONLY_FIELD_KEYS.has(field.value),
        pinned: field.value === "upc",
    }));
}

/**
 * @param {object} field
 * @returns {string[]|undefined}
 */
function resolveColumnChoices(field) {
    if (Array.isArray(field.choices) && field.choices.length) {
        return field.choices;
    }
    return UNIT_FIELD_CHOICES[field.value];
}

/**
 * @param {string|undefined} type
 * @param {string} value
 */
function mapFieldType(type, value) {
    if (value === "producttype") {
        return "producttype";
    }
    if (FORMAT_FIELD_KEYS.has(value)) {
        return "format";
    }
    if (UNIT_FIELD_CHOICES[value]) {
        return "choices";
    }
    if (
        type === "number" ||
        NUMBER_FIELD_KEYS.has(value) ||
        String(value).startsWith("initialstock.")
    ) {
        return "number";
    }
    if (type === "boolean") {
        return "boolean";
    }
    return "string";
}

function unitAndFormatFieldDefs() {
    return [
        { value: "stock_unit", text: "Stock unit" },
        {
            value: "piece_content_quantity",
            text: "Piece content quantity",
            type: "number",
        },
        { value: "piece_content_unit", text: "Piece content unit" },
        { value: "sale_unit", text: "Sale unit" },
        {
            value: "sale_unit_content_quantity",
            text: "Sale unit content quantity",
            type: "number",
        },
        { value: "sale_unit_content_unit", text: "Sale unit content unit" },
    ];
}

function defaultProviderProductFieldDefs() {
    return [
        { value: "upc", text: "UPC" },
        { value: "name", text: "Name" },
        { value: "unit_amount", text: "Purchase price", type: "number" },
        { value: "manufacturer", text: "Manufacturer" },
        { value: "packaging", text: "Packaging", type: "number" },
        ...unitAndFormatFieldDefs(),
        { value: "producttype", text: "Product type", type: "producttype" },
        { value: "is_service", text: "Is service", type: "boolean" },
    ];
}

function defaultProductsFieldDefs() {
    return [
        { value: "upc", text: "UPC" },
        { value: "name", text: "Name" },
        { value: "manufacturer", text: "Manufacturer" },
        {
            value: "default_unit_purchase_price_excl_vat",
            text: "Default purchase price",
            type: "number",
        },
        { value: "packaging", text: "Packaging", type: "number" },
        { value: "format_code", text: "Format code" },
        ...unitAndFormatFieldDefs(),
        { value: "sale_format_code", text: "Sale format code" },
        { value: "producttype", text: "Product type", type: "producttype" },
        { value: "is_service", text: "Is service", type: "boolean" },
    ];
}
